import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminPanel.css';

export default function AdminPanel() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('blogs');

  // Blogs state
  const [blogs, setBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(false);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [blogForm, setBlogForm] = useState({ title: '', excerpt: '', content: '', author: 'Shristiveda Team', category: 'महिला स्वास्थ्य', tags: '', is_published: 1 });
  const [blogImage, setBlogImage] = useState(null);
  const [editingBlog, setEditingBlog] = useState(null);
  const [blogMsg, setBlogMsg] = useState('');

  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);

  // Enquiries state
  const [enquiries, setEnquiries] = useState([]);

  // Accept optional creds so login can pass fresh values before state updates
  const getAuthHeader = (creds) => {
    const u = creds ? creds.username : credentials.username;
    const p = creds ? creds.password : credentials.password;
    const encoded = btoa(`${u}:${p}`);
    return { Authorization: `Basic ${encoded}` };
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    // Use local vars — React state may not have updated yet
    const freshCreds = { username: credentials.username, password: credentials.password };
    try {
      await axios.get('/api/blogs/admin/all', { headers: getAuthHeader(freshCreds) });
      setIsLoggedIn(true);
      setLoginError('');
      loadAll();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setLoginError('गलत username या password');
      } else if (!status) {
        setLoginError('❌ Server से connect नहीं हो पाया। Backend चल रहा है?');
      } else {
        setLoginError('❌ Error: ' + (err?.response?.data?.error || err.message));
      }
    }
  };

  const loadAll = () => {
    loadBlogs();
    loadTestimonials();
    loadEnquiries();
  };

  const loadBlogs = () => {
    setBlogsLoading(true);
    axios.get('/api/blogs/admin/all', { headers: getAuthHeader() })
      .then(r => { setBlogs(r.data); setBlogsLoading(false); })
      .catch(() => setBlogsLoading(false));
  };

  const loadTestimonials = () => {
    axios.get('/api/testimonials', { headers: getAuthHeader() })
      .then(r => setTestimonials(r.data));
  };

  const loadEnquiries = () => {
    axios.get('/api/enquiries', { headers: getAuthHeader() })
      .then(r => setEnquiries(r.data));
  };

  const handleBlogSubmit = async (e) => {
    e.preventDefault();
    setBlogMsg('');
    const formData = new FormData();
    Object.entries(blogForm).forEach(([k, v]) => formData.append(k, v));
    if (blogImage) formData.append('image', blogImage);

    try {
      if (editingBlog) {
        await axios.put(`/api/blogs/${editingBlog.id}`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
        setBlogMsg('✅ Blog updated successfully!');
      } else {
        await axios.post('/api/blogs', formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
        setBlogMsg('✅ Blog published successfully!');
      }
      setShowBlogForm(false);
      setEditingBlog(null);
      setBlogForm({ title: '', excerpt: '', content: '', author: 'Shristiveda Team', category: 'महिला स्वास्थ्य', tags: '', is_published: 1 });
      setBlogImage(null);
      loadBlogs();
    } catch (err) {
      setBlogMsg('❌ Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const editBlog = (blog) => {
    setEditingBlog(blog);
    setBlogForm({
      title: blog.title,
      excerpt: blog.excerpt || '',
      content: '',
      author: blog.author,
      category: blog.category,
      tags: blog.tags || '',
      is_published: blog.is_published,
    });
    setShowBlogForm(true);
    axios.get(`/api/blogs/${blog.slug}`)
      .then(r => setBlogForm(f => ({ ...f, content: r.data.content })));
  };

  const deleteBlog = async (id) => {
    if (!window.confirm('क्या आप इस ब्लॉग को हटाना चाहते हैं?')) return;
    await axios.delete(`/api/blogs/${id}`, { headers: getAuthHeader() });
    loadBlogs();
  };

  const approveTestimonial = async (id) => {
    await axios.put(`/api/testimonials/${id}/approve`, {}, { headers: getAuthHeader() });
    loadTestimonials();
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('Delete?')) return;
    await axios.delete(`/api/testimonials/${id}`, { headers: getAuthHeader() });
    loadTestimonials();
  };

  const markEnquiryRead = async (id) => {
    await axios.put(`/api/enquiries/${id}/read`, {}, { headers: getAuthHeader() });
    loadEnquiries();
  };

  if (!isLoggedIn) return (
    <div className="admin-login">
      <div className="login-box">
        <div className="login-logo">
          <span>🌿</span>
          <div>
            <strong>SHRISTIVEDA</strong>
            <small>Admin Panel</small>
          </div>
        </div>
        <h2>Login</h2>
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label>Username</label>
            <input type="text" value={credentials.username} onChange={e => setCredentials({ ...credentials, username: e.target.value })} required />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input type="password" value={credentials.password} onChange={e => setCredentials({ ...credentials, password: e.target.value })} required />
          </div>
          {loginError && <p className="form-error">{loginError}</p>}
          <button type="submit" className="btn-primary" style={{ width: '100%', justifyContent: 'center' }}>Login →</button>
        </form>
      </div>
    </div>
  );

  return (
    <div className="admin-panel">
      <div className="admin-sidebar">
        <div className="admin-logo">
          <span>🌿</span>
          <div>
            <strong>Shristiveda</strong>
            <small>Admin Panel</small>
          </div>
        </div>
        {['blogs', 'testimonials', 'enquiries'].map(tab => (
          <button
            key={tab}
            className={`sidebar-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'blogs' ? '📝 Blogs' : tab === 'testimonials' ? '⭐ Testimonials' : '📩 Enquiries'}
            {tab === 'enquiries' && enquiries.filter(e => !e.is_read).length > 0 && (
              <span className="badge">{enquiries.filter(e => !e.is_read).length}</span>
            )}
          </button>
        ))}
        <button className="sidebar-tab logout" onClick={() => { setIsLoggedIn(false); setCredentials({ username: '', password: '' }); }}>
          🚪 Logout
        </button>
      </div>

      <div className="admin-content">
        {/* BLOGS TAB */}
        {activeTab === 'blogs' && (
          <div>
            <div className="admin-header">
              <h2>📝 Blogs Management</h2>
              <button className="btn-primary" onClick={() => { setShowBlogForm(!showBlogForm); setEditingBlog(null); setBlogForm({ title: '', excerpt: '', content: '', author: 'Shristiveda Team', category: 'महिला स्वास्थ्य', tags: '', is_published: 1 }); }}>
                {showBlogForm ? '✕ बंद करें' : '+ नया ब्लॉग'}
              </button>
            </div>

            {blogMsg && <div className={`admin-msg ${blogMsg.includes('✅') ? 'success' : 'error'}`}>{blogMsg}</div>}

            {showBlogForm && (
              <div className="blog-form-box">
                <h3>{editingBlog ? 'Blog Edit करें' : 'नया Blog लिखें'}</h3>
                <form onSubmit={handleBlogSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>शीर्षक (Title) *</label>
                      <input value={blogForm.title} onChange={e => setBlogForm({ ...blogForm, title: e.target.value })} placeholder="ब्लॉग का शीर्षक" required />
                    </div>
                    <div className="form-group">
                      <label>Category</label>
                      <select value={blogForm.category} onChange={e => setBlogForm({ ...blogForm, category: e.target.value })}>
                        <option>महिला स्वास्थ्य</option>
                        <option>आयुर्वेदिक उत्पाद</option>
                        <option>घरेलू उपाय</option>
                        <option>पोषण</option>
                        <option>जीवनशैली</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>सारांश (Excerpt)</label>
                    <textarea value={blogForm.excerpt} onChange={e => setBlogForm({ ...blogForm, excerpt: e.target.value })} placeholder="ब्लॉग का संक्षिप्त विवरण" rows={2} />
                  </div>
                  <div className="form-group">
                    <label>मुख्य सामग्री (Content) * — HTML supported</label>
                    <textarea value={blogForm.content} onChange={e => setBlogForm({ ...blogForm, content: e.target.value })} placeholder="<h2>Heading</h2><p>Content...</p>" rows={12} style={{ fontFamily: 'monospace', fontSize: '0.85rem' }} required />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Author</label>
                      <input value={blogForm.author} onChange={e => setBlogForm({ ...blogForm, author: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label>Tags (comma separated)</label>
                      <input value={blogForm.tags} onChange={e => setBlogForm({ ...blogForm, tags: e.target.value })} placeholder="tag1, tag2" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Feature Image</label>
                      <input type="file" accept="image/*" onChange={e => setBlogImage(e.target.files[0])} />
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <select value={blogForm.is_published} onChange={e => setBlogForm({ ...blogForm, is_published: parseInt(e.target.value) })}>
                        <option value={1}>Published (Live)</option>
                        <option value={0}>Draft (Hidden)</option>
                      </select>
                    </div>
                  </div>
                  <button type="submit" className="btn-primary">{editingBlog ? '✓ Update Blog' : '🚀 Publish Blog'}</button>
                </form>
              </div>
            )}

            {blogsLoading ? <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div> : (
              <div className="admin-table">
                <table>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blogs.map(b => (
                      <tr key={b.id}>
                        <td><strong>{b.title}</strong></td>
                        <td><span className="tag">{b.category}</span></td>
                        <td><span className={`status-badge ${b.is_published ? 'published' : 'draft'}`}>{b.is_published ? 'Live' : 'Draft'}</span></td>
                        <td>{new Date(b.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className="action-btns">
                            <button className="action-btn edit" onClick={() => editBlog(b)}>✏️ Edit</button>
                            <button className="action-btn delete" onClick={() => deleteBlog(b.id)}>🗑️ Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {blogs.length === 0 && <div className="empty-state">कोई ब्लॉग नहीं है। पहला ब्लॉग लिखें!</div>}
              </div>
            )}
          </div>
        )}

        {/* TESTIMONIALS TAB */}
        {activeTab === 'testimonials' && (
          <div>
            <div className="admin-header"><h2>⭐ Testimonials</h2></div>
            <div className="admin-table">
              <table>
                <thead>
                  <tr><th>Name</th><th>Location</th><th>Review</th><th>Rating</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {testimonials.map(t => (
                    <tr key={t.id}>
                      <td><strong>{t.name}</strong></td>
                      <td>{t.location}</td>
                      <td style={{ maxWidth: '300px' }}><small>{t.review}</small></td>
                      <td>{'⭐'.repeat(t.rating || 5)}</td>
                      <td>
                        <div className="action-btns">
                          <button className="action-btn edit" onClick={() => approveTestimonial(t.id)}>✓ Approve</button>
                          <button className="action-btn delete" onClick={() => deleteTestimonial(t.id)}>🗑️ Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {testimonials.length === 0 && <div className="empty-state">कोई testimonial नहीं है।</div>}
            </div>
          </div>
        )}

        {/* ENQUIRIES TAB */}
        {activeTab === 'enquiries' && (
          <div>
            <div className="admin-header">
              <h2>📩 Enquiries</h2>
              <span style={{ color: 'var(--text-mid)', fontSize: '0.9rem' }}>
                {enquiries.filter(e => !e.is_read).length} unread
              </span>
            </div>
            <div className="admin-table">
              <table>
                <thead>
                  <tr><th>Name</th><th>Phone</th><th>Email</th><th>Message</th><th>Date</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {enquiries.map(e => (
                    <tr key={e.id} className={!e.is_read ? 'unread-row' : ''}>
                      <td><strong>{e.name}</strong></td>
                      <td><a href={`tel:${e.phone}`} style={{ color: 'var(--green)', fontWeight: 600 }}>{e.phone}</a></td>
                      <td>{e.email || '-'}</td>
                      <td style={{ maxWidth: '250px' }}><small>{e.message || '-'}</small></td>
                      <td><small>{new Date(e.created_at).toLocaleDateString()}</small></td>
                      <td>
                        {!e.is_read && (
                          <button className="action-btn edit" onClick={() => markEnquiryRead(e.id)}>✓ Mark Read</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {enquiries.length === 0 && <div className="empty-state">कोई enquiry नहीं है।</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
